import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ScrollTrigger, useGSAP } from '@/lib/gsap'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Counter } from '@/components/generals/counter'
import { Marquee } from '@/components/generals/marquee'
import { CortexMark } from '@/components/generals/logo'
import { Container } from '@/components/generals/container'
import { RollText } from '@/components/generals/roll-text'
import { CtaSection } from '@/components/generals/cta-section'
import { SectionLabel } from '@/components/generals/section-label'
import { TransitionLink } from '@/components/generals/transition-link'
import { SolutionVisual } from '@/components/landing/solutions/visuals'

import { SOLUTIONS } from '@/constants/solutions'

const Solutions: FC = (): ReactNode => {
    const bodyRef = useRef<HTMLDivElement>(null)
    const [active, setActive] = useState(0)

    // Highlight the solution currently in view in the side index
    useGSAP(() => {
        const sections = bodyRef.current?.querySelectorAll<HTMLElement>('[data-solution]')

        sections?.forEach((section, index) => {
            ScrollTrigger.create({
                trigger: section,
                start: 'top 55%',
                end: 'bottom 55%',
                onToggle: (self) => {
                    if (self.isActive) setActive(index)
                }
            })
        })
    }, { scope: bodyRef })

    return (
        <Layout>
            <Seo
                title='Solutions'
                description='AI agents, digital workflows, AI automation, business process automation, customer support, sales, internal operations and knowledge systems, built by Cortex.'
            />

            {/* Hero */}
            <section id='solutions-hero' aria-labelledby='solutions-title' className='relative pt-36 md:pt-48'>
                <Container>
                    <div data-reveal='fade'>
                        <SectionLabel>Solutions</SectionLabel>
                    </div>

                    <div className='mt-8 grid gap-10 lg:grid-cols-12 lg:items-end lg:gap-6'>
                        <h1 id='solutions-title' data-split className='type-display max-w-[13ch] lg:col-span-8'>
                            Where the busywork ends and the <span className='serif-accent'>real work</span> begins.
                        </h1>

                        <p data-reveal data-delay='0.2' className='type-lead text-soft lg:col-span-4'>
                            Eight ways Cortex takes the repetitive parts of support, sales, finance and operations off your team, with systems that read, decide and act the same way every time.
                        </p>
                    </div>

                    <nav aria-label='Solutions index' className='mt-16 md:mt-24'>
                        <ul data-reveal-group='0.05' className='grid border-t border-l border-line sm:grid-cols-2 lg:grid-cols-4'>
                            {SOLUTIONS.map((solution) => (
                                <li key={solution.slug} className='border-r border-b border-line'>
                                    <TransitionLink
                                        href={`#${solution.slug}`}
                                        className='group flex h-full items-center justify-between gap-4 p-5 transition-colors duration-500 hover:bg-white/[0.03]'
                                    >
                                        <span className='flex items-baseline gap-4'>
                                            <span className='type-label text-muted-foreground'>
                                                {solution.index}
                                            </span>

                                            <span className='text-[0.9375rem] text-foreground'>
                                                {solution.name}
                                            </span>
                                        </span>

                                        <ArrowUpRight className='size-4 shrink-0 text-muted-foreground transition-[color,transform] duration-500 ease-expo group-hover:rotate-90 group-hover:text-ember' />
                                    </TransitionLink>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </Container>

                <Marquee duration={50} className='mt-20 border-y border-line py-6 md:mt-28'>
                    {SOLUTIONS.map((solution) => (
                        <span key={solution.slug} className='flex items-center'>
                            <span className='px-8 font-heading text-[clamp(1.75rem,3.5vw,3rem)] tracking-[-0.04em] whitespace-nowrap text-foreground/70'>
                                {solution.name}
                            </span>

                            <CortexMark className='size-5 text-ember' />
                        </span>
                    ))}
                </Marquee>
            </section>

            {/* Solutions */}
            <div ref={bodyRef} className='relative'>
                <Container className='grid gap-6 py-20 md:py-28 lg:grid-cols-12'>
                    <aside className='hidden lg:col-span-3 lg:block'>
                        <div className='sticky top-28'>
                            <p className='type-label text-muted-foreground'>
                                Index
                            </p>

                            <ol className='mt-6 flex flex-col border-t border-line'>
                                {SOLUTIONS.map((solution, index) => (
                                    <li key={solution.slug}>
                                        <TransitionLink
                                            href={`#${solution.slug}`}
                                            aria-current={active === index ? 'true' : undefined}
                                            className={cn(
                                                'group flex items-center gap-3 border-b border-line py-3 text-sm transition-colors duration-500',
                                                active === index ? 'text-foreground' : 'text-muted-foreground hover:text-soft'
                                            )}
                                        >
                                            <span
                                                className={cn(
                                                    'h-px bg-ember transition-all duration-700 ease-expo',
                                                    active === index ? 'w-4 opacity-100' : 'w-0 opacity-0'
                                                )}
                                            />

                                            <span className='font-mono text-[11px]'>
                                                {solution.index}
                                            </span>

                                            <span className='truncate'>
                                                {solution.name}
                                            </span>
                                        </TransitionLink>
                                    </li>
                                ))}
                            </ol>

                            <p className='type-label mt-6 text-muted-foreground tabular-nums'>
                                {String(active + 1).padStart(2, '0')} / {String(SOLUTIONS.length).padStart(2, '0')}
                            </p>
                        </div>
                    </aside>

                    <div className='flex flex-col lg:col-span-9'>
                        {SOLUTIONS.map((solution, index) => {
                            const reversed = index % 2 === 1

                            return (
                                <section
                                    key={solution.slug}
                                    id={solution.slug}
                                    data-solution
                                    aria-labelledby={`${solution.slug}-title`}
                                    className='scroll-mt-28 border-t border-line py-16 first:border-t-0 first:pt-0 md:py-24'
                                >
                                    <div className='flex items-center justify-between gap-6'>
                                        <span className='type-label text-ember'>
                                            Solution {solution.index}
                                        </span>

                                        <span className='type-label text-muted-foreground'>
                                            {solution.index} / 08
                                        </span>
                                    </div>

                                    <h2 id={`${solution.slug}-title`} data-split className='type-h2 mt-6 max-w-[16ch]'>
                                        {solution.name}
                                    </h2>

                                    <p data-reveal className='serif-accent mt-4 text-[clamp(1.4rem,2.2vw,2rem)] leading-tight text-soft'>
                                        {solution.tagline}
                                    </p>

                                    <div className='mt-10 grid gap-10 md:grid-cols-2 md:gap-8 xl:gap-12'>
                                        <div className={cn('flex flex-col gap-10', reversed && 'md:order-2')}>
                                            <p data-reveal className='text-soft'>
                                                {solution.description}
                                            </p>

                                            <div data-reveal>
                                                <p className='type-label text-muted-foreground'>
                                                    What it handles
                                                </p>

                                                <ul className='mt-4 border-t border-line'>
                                                    {solution.handles.map((item) => (
                                                        <li
                                                            key={item}
                                                            className='group flex items-center justify-between gap-4 border-b border-line py-3 text-[0.9375rem] text-foreground transition-colors duration-300 hover:text-ember'
                                                        >
                                                            <span className='flex items-center gap-3'>
                                                                <Check className='size-4 text-ember' />
                                                                {item}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>

                                            <dl data-reveal className='grid grid-cols-2 gap-6'>
                                                {solution.outcomes.map((outcome) => (
                                                    <div key={outcome.label} className='flex flex-col-reverse justify-end gap-2 border-l border-line pl-4'>
                                                        <dt className='text-sm text-soft'>
                                                            {outcome.label}
                                                        </dt>

                                                        <dd className='font-heading text-[clamp(2rem,3.4vw,3rem)] leading-none tracking-[-0.045em] text-foreground'>
                                                            <Counter
                                                                value={outcome.value}
                                                                decimals={outcome.decimals}
                                                                prefix={outcome.prefix}
                                                                suffix={outcome.suffix}
                                                            />
                                                        </dd>
                                                    </div>
                                                ))}
                                            </dl>

                                            <TransitionLink
                                                href={`/contact?interest=${solution.slug}`}
                                                className='group inline-flex items-center gap-2 self-start text-[0.9375rem] text-foreground'
                                            >
                                                <RollText>{`Discuss ${solution.name}`}</RollText>
                                                <ArrowRight className='size-4 text-ember transition-transform duration-500 ease-expo group-hover:translate-x-1' />
                                            </TransitionLink>
                                        </div>

                                        <div data-reveal='scale' className={cn(reversed && 'md:order-1')}>
                                            <SolutionVisual
                                                type={solution.visual}
                                                image={solution.image}
                                                imageAlt={solution.imageAlt}
                                            />
                                        </div>
                                    </div>
                                </section>
                            )
                        })}
                    </div>
                </Container>
            </div>

            <CtaSection
                label='Find your starting point'
                title={(
                    <>
                        Not sure where to <span className='serif-accent'>start?</span>
                    </>
                )}
                description='Most clients begin with one process that costs too much time. Tell us about yours and we will show you what automating it could return.'
                secondary={{ label: 'See our services', href: '/services' }}
            />
        </Layout>
    )
}

export default Solutions
